import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  GroupButtonComponent,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  TableHeader
} from 'mbs-ui-kit';
import { LicensesPricesTableComponent } from 'mbs-ui-shared@components/buy-license-modal/components/licenses-prices-table/licenses-prices-table.component';
import { getTableHeaders } from 'mbs-ui-shared@components/buy-license-modal/components/licenses-prices-table/utils/table';
import { RequestQuoteModalComponent } from 'mbs-ui-shared@components/buy-license-modal/components/request-quote-modal/request-quote-modal.component';
import {
  LicensesCount,
  LicensesPricesData,
  LicenseTypePrice,
  PriceDetails,
  TotalPrice
} from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { Balance } from 'mbs-ui-shared@components/components/models/license.model';
import { LicensesSharedModule } from 'mbs-ui-shared@components/licenses/licenses-shared.module';
import { LicensesPipesModule } from 'mbs-ui-shared@components/licenses/pipes/licenses-pipes.module';
import { BALANCE_TOKEN } from 'mbs-ui-shared@components/licenses/tokens/balance.token';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import { SubscriptionLicenseMode } from 'mbs-ui-shared@models/License';
import { LicenseMode } from 'mbs-ui-shared@models/ProviderData';
import { UtilsModule } from 'mbs-ui-shared@utils/utils.module';
import { finalize, Observable, of } from 'rxjs';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-buy-license-modal',
  templateUrl: './buy-license-modal.component.html',
  styleUrls: ['./buy-license-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    ButtonComponent,
    LicensesPipesModule,
    LicensesSharedModule,
    NgbTooltipModule,
    RouterLink,
    LicensesPricesTableComponent,
    GroupButtonComponent,
    UtilsModule,
    ModalComponent,
    I18NextPipe,
    ModalFooterDirective
  ]
})
export class BuyLicenseModalComponent implements OnInit {
  private licensesFacade = inject(LicensesFacade);
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private i18n = inject(I18NextService);
  private modalService = inject(ModalService);
  private ability = inject(AbilityService);
  private authFacade = inject(AuthFacade);
  balance = inject<Observable<Balance>>(BALANCE_TOKEN);

  private readonly baseModal = viewChild.required(ModalComponent);

  public readonly mbsPopupType = MbsPopupType;

  public readonly elementsSelector = {
    name: {
      postPaymentInfoAlert: 'buy-license-modal_post-payment-info'
    }
  };

  public headers: Array<TableHeader>;

  public licensesCountForm = new FormGroup({});

  public permissionFlags = this.licensesFacade.permissionFlags;
  public isPostPayment = this.licensesSignalFacade.isPostPayment;

  public licensesData: WritableSignal<LicensesPricesData> = signal(null);
  public initLicensesData: WritableSignal<LicensesPricesData> = signal(null);
  public loading = signal(true);
  public buyLoading = signal(false);

  public licensesCounts = signal<LicensesCount>(null);

  public totalListPrice = signal(0);
  public totalProratedPrice = signal(0);
  public totalDiscountedPrice = signal(0);
  public totalRenewalDate = signal(null);
  public loadingDiscountedPrice = signal(false);
  public totalDiscount = computed(() => Math.max(0, this.totalProratedPrice() - this.totalDiscountedPrice()));
  public totalDiscountPercent = computed(() => {
    const proratedPrice = this.totalProratedPrice();

    return proratedPrice ? (this.totalDiscount() / proratedPrice) * 100 : 0;
  });

  public minCommitment = signal(0);

  public readonly title = computed(() =>
    this.i18n.t(`shared:licenses.buyLicenseModal.title.${this.isPostPayment() ? 'costEstimation' : 'buy'}`)
  );
  public isMonthlyPayment = signal(true);
  public isBuyAvailable = signal(false);
  public isDiscountedPriceShown = signal(true);
  private readonly discountExcludedLicenseTypes = this.getDiscountExcludedLicenseTypes();
  public readonly discountExclusionParams = computed(() => ({
    licenseTypes: this.discountExcludedLicenseTypes
      .map((type) => this.licensesSignalFacade.allLicenseTypesMap().get(type))
      .filter(Boolean)
      .join(', '),
    interpolation: { escapeValue: false }
  }));

  ngOnInit(): void {
    this.setHeaders();
    this.setLicenseTypes();
    this.isMonthlyPayment.set(this.isCurrentModeMonthly());
    this.isDiscountedPriceShown.set(this.getIsDiscountedPriceShown());
  }

  public buy() {
    this.buyLoading.set(true);
    this.licensesFacade
      .getPurchase(this.getPayloadForPurchase())
      .pipe(finalize(() => this.buyLoading.set(false)))
      .subscribe((result) => {
        if (result?.link) {
          location.href = result.link.replace(/^~\//, '/');
        } else {
          this.baseModal().save();
        }
      });
  }

  public setPaymentPeriod(isMonthly: boolean) {
    if (this.isMonthlyPayment() === isMonthly) return;

    this.isMonthlyPayment.set(isMonthly);
    this.setPrices(true).subscribe();
  }

  public requestQuote() {
    this.buyLoading.set(true);
    this.licensesFacade
      .getPurchase(this.getPayloadForPurchase())
      .pipe(finalize(() => this.buyLoading.set(false)))
      .subscribe(() => {
        this.modalService
          .openCustom(RequestQuoteModalComponent, {
            size: MbsSize.md,
            data: {
              reseller: this.licensesData()?.reseller
            }
          })
          .then(() => this.baseModal().save())
          .catch(() => this.baseModal().save());
      });
  }

  public onCountChanged(value: LicensesCount) {
    this.licensesCounts.set(value);

    return this.setPrices();
  }

  public isProratedPriceFinal() {
    const version = this.getLicenseModeVersion(this.getLicenseMode());

    return version == 3 || version == 2;
  }

  public isDiscountSummaryShown() {
    return this.isPostPayment() || this.getLicenseModeVersion(this.getLicenseMode()) === 4;
  }

  private getDiscountedPrice() {
    return this.licensesFacade.getTotals(this.getPayload(false));
  }

  private getPayload(isPurchase = true) {
    const payload = { licenseTypesCounts: this.getTotalPayload(this.licensesCounts()) };
    if (this.licensesData().isFirstPurchase) {
      payload[isPurchase ? 'newLicenseMode' : 'licenseMode'] = this.getNewLicenseMode();
    }

    return payload;
  }

  private getLicenseModeVersion(licenseMode: LicenseMode) {
    const versions = {
      [LicenseMode.CumulativeV4Annual]: 4,
      [LicenseMode.CumulativeV4Monthly]: 4,
      [LicenseMode.CumulativeV3Annual]: 3,
      [LicenseMode.CumulativeV3Monthly]: 3,
      [LicenseMode.CumulativeV2Annual]: 2,
      [LicenseMode.CumulativeV2Monthly]: 2,
      [LicenseMode.Monthly]: 1,
      [LicenseMode.Yearly]: 1
    };

    return versions[licenseMode] ?? 0;
  }

  private getIsDiscountedPriceShown() {
    return !this.isDiscountSummaryShown();
  }

  private getDiscountExcludedLicenseTypes(): LicenseType[] {
    const isRmmPerAdmin = !this.ability.can('read', PermissionsEnum.RmmPerEndpoint);
    const isDeepInstinct =
      this.ability.can('read', PermissionsEnum.ShowDeepInstinct) && this.ability.can('read', PermissionsEnum.ShowDeepInstinctProvider);

    return [
      ...(isRmmPerAdmin ? [LicenseType.RMM, LicenseType.RemoteDesktop] : []),
      ...(isDeepInstinct ? [LicenseType.DeepInstinct] : []),
      LicenseType.PremiumSupport,
      LicenseType.RestoreToCloud
    ];
  }

  private getLicenseMode() {
    return this.authFacade.currentUserValue?.ProviderInfo?.LicenseMode;
  }

  private getNewLicenseMode() {
    const licenseMode = this.getLicenseMode();

    if (this.isPostPayment()) {
      return SubscriptionLicenseMode.PostPaymentV1Monthly;
    }

    const currentVersion = this.getLicenseModeVersion(licenseMode);

    const licenseModes = {
      1: [SubscriptionLicenseMode.Monthly, SubscriptionLicenseMode.Yearly],
      2: [SubscriptionLicenseMode.CumulativeV2Monthly, SubscriptionLicenseMode.CumulativeV2Annual],
      3: [SubscriptionLicenseMode.CumulativeV3Monthly, SubscriptionLicenseMode.CumulativeV3Annual],
      4: [SubscriptionLicenseMode.CumulativeV4Monthly, SubscriptionLicenseMode.CumulativeV4Annual]
    };

    return licenseModes[currentVersion][this.isMonthlyPayment() ? 0 : 1];
  }

  private isCurrentModeMonthly(): boolean {
    const licenseMode = this.getLicenseMode();

    return [
      LicenseMode.Monthly,
      LicenseMode.CumulativeV2Monthly,
      LicenseMode.CumulativeV3Monthly,
      LicenseMode.CumulativeV4Monthly,
      LicenseMode.PostPaymentV1Monthly
    ].includes(licenseMode);
  }

  private getPayloadForPurchase() {
    const payload = this.getPayload();
    const hid = this.baseModal().data.hid;
    if (hid) {
      payload['autoAssignLicenses'] = [
        {
          licenseType: this.baseModal().data.licenseType,
          hid: hid
        }
      ];
    }
    return payload;
  }

  private setPrices(useLoader = false) {
    if (!this.licensesCounts()) {
      return of(null);
    }
    if (useLoader) {
      this.loadingDiscountedPrice.set(true);
    }
    return this.getDiscountedPrice().pipe(
      tap(({ totalDiscountedPrice, licenseTypesPrices, minCommitment, renewalDate }) => {
        this.licensesData.update((value) => {
          const updatedPrices = this.getUpdatedPrices(licenseTypesPrices, value.licenseTypesPrices);

          return {
            ...value,
            licenseTypesPrices: updatedPrices,
            licenseTypesPricesMap: this.licensesFacade.getPricesMap(updatedPrices)
          };
        });

        const { totalListPrice, totalProratedPrice } = this.calculatePrices();

        this.totalDiscountedPrice.set(totalDiscountedPrice);
        this.totalListPrice.set(totalListPrice);
        this.totalProratedPrice.set(totalProratedPrice);
        this.minCommitment.set(minCommitment);
        this.totalRenewalDate.set(renewalDate);
        this.isBuyAvailable.set(Boolean(this.totalListPrice()));
      }),
      finalize(() => useLoader && this.loadingDiscountedPrice.set(false))
    );
  }

  private getUpdatedPrices(newPrices: Array<TotalPrice>, prices: Array<LicenseTypePrice>) {
    const getPricesMap = (prices) => Object.fromEntries(prices.map((item) => [item.type, item.prices]));
    const pricesMap = getPricesMap(newPrices);

    const period = this.isMonthlyPayment() ? 'monthlyPrices' : 'annualPrices';

    return prices.map((price) => {
      const initPrice = this.initLicensesData().licenseTypesPricesMap[price.licenseType]?.[period];
      const newPrice = pricesMap[price.licenseType] || initPrice;

      return {
        ...price,
        [period]: {
          ...newPrice,
          basePrice: initPrice.basePrice,
          specialPrice: initPrice.specialPrice
        }
      };
    }) as Array<LicenseTypePrice>;
  }

  private calculatePrices() {
    return this.licensesData().licenseTypesPrices.reduce(
      (acc, { licenseType }) => {
        const { unitListPrice, unitProratedPrice } = this.getPrice(licenseType);
        const count = this.licensesCounts()[licenseType] ?? 0;

        acc.totalListPrice += unitListPrice * count;
        acc.totalProratedPrice += unitProratedPrice * count;

        return acc;
      },
      { totalListPrice: 0, totalProratedPrice: 0 }
    );
  }

  private getPrice(licenseType: LicenseType): PriceDetails {
    const license = this.licensesData().licenseTypesPricesMap[licenseType];
    return this.isMonthlyPayment() ? license.monthlyPrices : license.annualPrices;
  }

  private getTotalPayload(value: LicensesCount) {
    return (
      value &&
      Object.entries(value)
        .filter(([_, count]) => count)
        .map(([key, count]) => ({
          type: Number(key),
          count: Number(count),
          existCount: this.licensesData().licenseTypesPricesMap[key].existCount
        }))
    );
  }

  private setHeaders() {
    this.headers = getTableHeaders(this.i18n);
  }

  private setLicenseTypes() {
    this.licensesFacade
      .getPrices(this.baseModal().data.allowedLicenses)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((data) => {
        this.licensesData.set(data);
        this.initLicensesData.set(data);
        this.initForm();
      });
  }

  private initForm() {
    this.licensesData().licenseTypesPrices.forEach((licenseType) => {
      this.licensesCountForm.addControl(String(licenseType.licenseType), new FormControl(this.baseModal().data.preselected ? 1 : 0));
    });
    this.licensesCountForm.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(this.licensesCountForm.value),
        debounceTime(350),
        switchMap((value: LicensesCount) => this.onCountChanged(value))
      )
      .subscribe();
  }
}
